/**
 * Gli editoriali finché a monte il tipo "Editoriale" non esiste.
 *
 * A monte il tipo "Editoriale" non esiste ancora, quindi la pagina
 * /editoriali non avrebbe niente da mostrare e non si potrebbe
 * giudicare l'impaginazione. I dieci provvisori servono a quello e solo
 * a quello: sono testi neutri scritti per riempire lo spazio, non pezzi
 * dell'Osservatorio, e non contengono fatti, date o attribuzioni che
 * possano essere presi per veri.
 *
 * In testa all'elenco ci sono però due pezzi veri, "Il mercato
 * speculativo" (04/11/2026) ed "E intanto si vende" (20/09/2026), che
 * sono pubblicati per essere letti: si riconoscono da `provvisorio: false`,
 * che gli toglie la nota di redazione e il `noindex` della rotta di
 * lettura. È qui, in mezzo ai provvisori, perché il tipo a monte non
 * c'è e questa è l'unica strada per cui un editoriale arriva in pagina.
 * Quando il tipo esiste, quel pezzo va portato su Strapi come dispaccio
 * di tipo "Editoriale" e il file si cancella con tutto il resto.
 *
 * Le lunghezze dei titoli sono quelle vere, non quelle comode: nel
 * rullo la mediana è di 52 caratteri e il novantesimo percentile di 90.
 * Tre di questi dieci stanno apposta sopra i 74 caratteri, altrimenti
 * il blocco editoriale della prima pagina si giudicherebbe su titoli
 * corti che a monte non esistono e i tagli si scoprirebbero in
 * produzione.
 *
 * Appena su Strapi compare il primo dispaccio di tipo "Editoriale", la
 * pagina passa a quelli e questi non vengono più renderizzati. Quando
 * ce ne sono abbastanza, questo file si cancella: non è usato da
 * nient'altro. Il corpo qui è dichiarato a blocchi (vedi
 * `BloccoEditoriale`): a monte sarà un testo solo, quindi quella forma
 * muore insieme al file.
 */

import { slugify } from "@/lib/slug";

/* Il blocco del corpo (`BloccoEditoriale`) e l'immagine d'apertura
   (`CopertinaLettura`) sono dichiarati in lib/editoriale-lettura.ts, il
   modello della pagina di lettura, che li condivide col ramo del CMS: la
   resa non deve morire insieme a questo file. Qui si riesporta il blocco
   per chi lo importava da qui. */
import type { BloccoEditoriale, CopertinaLettura, SchedaRedazione } from "@/lib/editoriale-lettura";
export type { BloccoEditoriale };

/** Il testo di un pezzo, in prosa e in ordine di lettura.
 *
 *  Serve a chi vuole le parole e non l'impaginazione: il tempo di
 *  lettura e la copia negli appunti. Il titolo di una sezione e quello
 *  di un riquadro ci stanno dentro perché si leggono anche loro. */
export function testoEditoriale(corpo: BloccoEditoriale[]): string[] {
  return corpo.flatMap((b) => {
    if (typeof b === "string") return [b];
    switch (b.tipo) {
      case "sezione":
        return [b.titolo];
      case "riquadro":
        return [b.titolo, ...b.testo];
      /* Il segnaposto non porta testo: la domanda è un campo a sé. Lo
         stacco è una pausa, non parole. */
      case "controdomanda":
      case "stacco":
        return [];
      default:
        return [b.testo];
    }
  });
}

/** L'attacco, cioè il primo capoverso del pezzo.
 *
 *  Lo chiede la prima pagina, che al sommario aggiunge l'inizio del
 *  testo per riempire la colonna. Con i blocchi `corpo[0]` non è più
 *  detto che sia un capoverso, e un titolo di sezione attaccato al
 *  sommario si leggerebbe come una frase monca. */
export function attaccoEditoriale(corpo: BloccoEditoriale[]): string {
  const primo = corpo.find((b) => typeof b === "string");
  return typeof primo === "string" ? primo : "";
}

/** Una foto nel margine sinistro della pagina di lettura.
 *
 *  Il margine sinistro esiste già come colonna della griglia di
 *  `/editoriali/{id}/{slug}` ed era vuoto: è lo spazio dove un
 *  quotidiano mette le foto piccole, quelle che accompagnano il pezzo
 *  senza interromperlo. Per questo la foto non entra nel filo del
 *  testo ma gli sta accanto: chi legge non deve saltarla per arrivare
 *  al capoverso dopo. */
export type EditorialeImmagine = {
  /** L'indirizzo della foto. Se è remoto, l'host deve stare fra i
   *  `remotePatterns` di `next.config.ts`, altrimenti `<Image>` la
   *  rifiuta. Il riquadro è in rapporto 3:2, la misura in cui escono le
   *  foto dei dispacci: un'immagine con un altro rapporto viene
   *  ritagliata. */
  src: string;
  /** La didascalia, sotto la foto e allineata a destra. */
  didascalia: string;
  /** Il credito, sotto la didascalia. `null` quando non c'è: nei
   *  provvisori dice sempre che l'immagine è di riempimento, per la
   *  stessa ragione per cui il testo lo dichiara in fondo alla pagina. */
  credito: string | null;
  /** Dopo quale paragrafo di `corpo` va la foto, contando da 1: `1` la
   *  mette accanto al secondo paragrafo, cioè dopo il primo. `0` la
   *  porta in cima, di fianco all'inizio del pezzo; un numero oltre
   *  l'ultimo paragrafo la lascia in fondo.
   *
   *  È un ancoraggio, non una posizione esatta: la foto sta fuori dal
   *  flusso e non allunga il testo, quindi due foto ancorate a
   *  paragrafi vicini si sovrappongono. Vanno distanziate qui. */
  dopoParagrafo: number;
};

export type EditorialeDemo = {
  id: string;
  titolo: string;
  /* L'occhiello, cioè la parola che in testata annuncia il pezzo prima
     del titolo, qui **non c'è**, ed è una scelta e non una dimenticanza:
     una rubrica scritta a mano nel repo è una parola che nessuna
     redazione ha deciso, stampata in evidenza come se l'avesse decisa.
     Ce n'era una, "Frattura", sul pezzo qui sotto, ed è uscita il
     20/09/2026 su richiesta.

     Quando il tipo "Editoriale" esiste a monte l'occhiello lo dà
     `category` del dispaccio, che è un dato vero: lo legge
     `app/editoriali/page.tsx`, e la riga di testata in vetrina lo
     stampa in coda a destra. */
  sommario: string;
  categoria: string;
  /** Il Paese di riferimento, quando il pezzo ne ha uno.
   *  Serve alla riga "categoria · paese · data" del blocco editoriale
   *  in prima pagina: senza, il fallback non permetterebbe di
   *  giudicare l'impaginazione. Qualcuno è `null` di proposito, perché
   *  a monte il campo manca spesso e il caso va visto. */
  paese: string | null;
  /** La foto di apertura: quella in testa alla colonna di lettura di
   *  `/editoriali/{id}/{slug}` e quella della scheda nell'elenco della
   *  sezione. `null` quando il pezzo non ne ha, ed è il caso più
   *  frequente: senza foto la pagina si apre dal titolo e la scheda
   *  resta di solo testo, non c'è nessun segnaposto.
   *
   *  È un campo a sé e non la prima delle `immagini`. Quelle nascono
   *  come foto di margine, ancorate a un paragrafo, e nel margine non si
   *  impaginano più: leggerne la prima come apertura legava due cose che
   *  non c'entrano, e toccando l'ancoraggio di una foto di margine
   *  cambiava l'apertura del pezzo. Corrisponde a `image_url` a monte,
   *  che è il campo da cui la prenderanno gli editoriali veri.
   *
   *  Se è remoto, l'host deve stare fra i `remotePatterns` di
   *  `next.config.ts`. Il riquadro è in rapporto 3:2, la misura in cui
   *  escono le foto dei dispacci. */
  foto: string | null;
  /** L'immagine d'apertura della pagina di lettura, con la sua misura.
   *
   *  È un campo a sé e non `foto`, che resta quella delle schede
   *  dell'indice e della prima pagina: quei riquadri hanno un rapporto
   *  loro e ritagliano, mentre la pagina di lettura mostra l'immagine
   *  intera nel suo rapporto (vedi `CopertinaLettura`). Portare
   *  l'illustrazione anche nell'indice è una scelta di quella pagina, non
   *  di questa. Sui pezzi veri del CMS l'immagine è `image_url`. */
  copertina?: CopertinaLettura;
  /** La data di uscita del pezzo, `AAAA-MM-GG`, quando il pezzo è
   *  uscito davvero.
   *
   *  È la data che la pagina stampa, e non cambia più: un pezzo
   *  pubblicato il 20 settembre porta il 20 settembre anche a
   *  dicembre. Prima la data la dava `settimaneFa`, che si conta a
   *  ritroso dal giorno in cui si guarda la pagina: sul pezzo in cima,
   *  che sta a zero settimane, voleva dire che l'editoriale risultava
   *  uscito **oggi**, qualunque fosse il giorno, e la sua data si
   *  spostava da sola ogni notte.
   *
   *  Corrisponde a `datetime` a monte, cioè al campo da cui la
   *  prenderanno gli editoriali veri. Manca sui provvisori, che una
   *  data di pubblicazione non ce l'hanno: la loro se la conta
   *  `tempoEditoriale`. */
  pubblicato?: string;
  /** Settimane indietro rispetto all'ultima uscita, per l'ordinamento
   *  e per la data dei provvisori. Su un pezzo con `pubblicato` non si
   *  legge: là la data è scritta. */
  settimaneFa: number;
  /** Il testo del pezzo, in paragrafi.
   *  Serve alla pagina di lettura /editoriali/{id}/{slug}: senza
   *  corpo, il
   *  clic su un editoriale porterebbe a un titolo e tre righe, che non
   *  è "l'articolo per intero". Valgono le stesse regole del resto del
   *  file: prosa di metodo, nessun fatto, nessuna data, nessun nome. */
  corpo: BloccoEditoriale[];
  /** Il pezzo è uno dei provvisori di riempimento?
   *
   *  Manca su tutti i provvisori e si legge come `voce.provvisorio !==
   *  false`, cioè: provvisorio finché qualcuno non dice il contrario.
   *  È la stessa forma del `free` a monte, e per la stessa ragione: il
   *  caso normale non deve dover essere scritto dieci volte.
   *
   *  Chi lo mette a `false` dichiara un pezzo pubblicato, e la rotta di
   *  lettura gli toglie due cose: la nota di redazione che avverte che
   *  il testo è di riempimento, e il `noindex`. Sono i due segni che
   *  distinguono una pagina di prova da una pagina pubblicata, e su un
   *  pezzo vero direbbero il falso. */
  provvisorio?: boolean;
  /** I capoversi di una sezione si fondono in uno (`capoversiUniti` di
   *  `PezzoLettura`)? Manca, cioè vero, sui provvisori e su «E intanto si
   *  vende», scritti a frasi brevi per quella regola. `false` quando ogni
   *  capoverso della consegna è della redazione e va tenuto a sé, come sui
   *  pezzi del CMS: «Il mercato speculativo» apre tre capoversi con
   *  «Moda.», «Automobile.», «Alimentare.», e fusi si leggevano come una
   *  frase sola. */
  capoversiUniti?: boolean;
  /** Le foto nel margine, quando il pezzo ne ha. Opzionale perché la
   *  maggior parte dei pezzi non ne ha nessuna.
   *
   *  Oggi non le rende nessuno: nel margine non si impaginano più (il
   *  perché sta nella colonna di sinistra di
   *  `/editoriali/{id}/{slug}`) e l'apertura la dà `foto`. I dati
   *  restano qui perché descrivono un'impaginazione che potrebbe
   *  tornare, non perché qualcuno li legga. */
  immagini?: EditorialeImmagine[];
  /** «La controdomanda»: una domanda che rovescia la tesi del pezzo,
   *  resa sotto l'editoriale della settimana nella raccolta
   *  (app/editoriali). Facoltativa: senza, la fascia non
   *  compare e non lascia vuoti. A monte il campo non esiste ancora; la
   *  specifica per Strapi è in docs/strapi-campo-controdomanda.md. */
  controdomanda?: string;
  /** I tre blocchi della scheda nella colonna destra della pagina di
   *  lettura («La frattura», «La catena della cessione», «Che cosa si
   *  compra»). Li scrive la redazione per ciascun pezzo; dove mancano, la
   *  scheda si ferma ai dati e a «Nel testo». */
  frattura?: SchedaRedazione["frattura"];
  catena?: string[];
  siCompra?: string[];
  /** Le etichette di quei blocchi, quando il tema ne vuole di sue
   *  (`SchedaRedazione["etichette"]`). */
  etichette?: SchedaRedazione["etichette"];
};

/** L'indirizzo della pagina di lettura di un provvisorio.
 *
 *  Stessa forma di `articleHref`, id e poi titolo: nel resto del sito
 *  un pezzo sta a `/article/{id}/{slug}` e il titolo è dentro
 *  l'indirizzo, perché è quello che un motore di ricerca legge e
 *  quello che si capisce incollando un link in chat. Questi non hanno
 *  un dispaccio dietro e quindi non stanno sotto `/article`, ma la
 *  forma dell'indirizzo è la stessa, così non ci sono due grammatiche
 *  di URL nello stesso sito.
 *
 *  Sta qui e non nelle pagine perché la costruiscono in tre posti,
 *  prima pagina, archivio e la pagina stessa per l'indirizzo
 *  canonico: tre copie a mano divergono, come è già successo con le
 *  tabelle delle priorità. */
export function editorialeDemoHref(e: Pick<EditorialeDemo, "id" | "titolo">): string {
  return `/editoriali/${e.id}/${slugify(e.titolo)}`;
}

export const EDITORIALI_DEMO: EditorialeDemo[] = [
  /* Il pezzo vero, e l'unico: `provvisorio: false`. Sta in prima
     posizione perché l'elenco è in ordine di pubblicazione e la pagina
     /editoriali mette in apertura il primo, quindi qui "primo" vuol dire
     "l'ultimo uscito". I dieci provvisori sono stati arretrati di una
     settimana ciascuno per fargli posto: con due pezzi a `settimaneFa:
     0` la sezione avrebbe mostrato due editoriali della stessa data, e
     ne esce uno alla settimana. */
  /* Il secondo pezzo vero, uscito il 04/11/2026: va in testa perché è
     l'ultimo uscito (l'ordine lo dà comunque `pubblicato`, non la
     posizione). Testo riportato parola per parola dalla consegna,
     con tre refusi corretti il 04/11/2026 su richiesta del
     committente, solo nel capoverso «Moda.»: «di generazione in
     generazione», «hanno alimentato / hanno danneggiato» (due soggetti),
     apostrofi tipografici come nel resto del testo. Nessuna firma nel testo,
     quindi quella della casa (`firmaDi(null)`); niente citazioni né
     controdomanda, che il testo non ha. Le tre righe di sezione della
     consegna sono blocchi `sezione`; «Moda.», «Automobile.»,
     «Alimentare.» restano in testa al loro capoverso, perché il modello
     dei blocchi non ha un attacco in grassetto. */
  {
    id: "mercato-speculativo",
    titolo: "Il mercato speculativo",
    sommario:
      "Come burocrazia e finanza possono indebolire il valore del prodotto e lasciare spazio al falso",
    categoria: "Economia",
    paese: null,
    /* La foto delle anteprime (card di /editoriali, «altri editoriali»,
       colonna dei precedenti): la versione quadrata, 1254 × 1254,
       consegnata il 04/11/2026 (uploads/image.png, copiata tale e quale)
       perché nei riquadri alti la 2,5:1 tagliava via il volto bianco.
       Aprendo il pezzo resta la `copertina` larga qui sotto. */
    /* La foto della card (indice /editoriali, «altri editoriali», colonna
       dei precedenti): la versione quadrata della copertina con la cascata,
       1254 × 1254, consegnata il 04/11/2026 (uploads/image.png, copiata tale
       e quale). Nei riquadri alti della card una quadrata mostra intere le
       due facce, la 2,5:1 le tagliava. */
    foto: "/editoriali/mercato-speculativo-card.png",
    /* La copertina dell'articolo, sostituita il 04/11/2026 su richiesta
       (uploads/image.png della seconda consegna, copiata tale e quale;
       la prima, le due teste su fondo nero, è uscita): 1983 × 793, cioè lo stesso
       2,5:1 della copertina di OI-353919, per cui i `sizes` delle
       anteprime sono già calcolati. */
    copertina: {
      src: "/editoriali/mercato-speculativo-copertina.png",
      larghezza: 1983,
      altezza: 793,
      alt: "Due volti scolpiti nella roccia, uno chiaro e uno scuro, a occhi chiusi, separati da una cascata d’acqua.",
    },
    pubblicato: "2026-11-04",
    settimaneFa: 0,
    provvisorio: false,
    capoversiUniti: false,
    /* La controdomanda, chiesta dal committente il 04/11/2026 («come
       stanno negli altri editoriali»): il testo consegnato non ne aveva
       una, quindi è scritta qui e **da confermare** dalla redazione.
       Rovescia la tesi: non sono finanza e burocrazia ad aprire lo spazio
       al falso, è il compratore che non paga più la differenza. Tetto
       del campo Strapi 220 caratteri; quando il pezzo passa su Strapi va
       copiata là tale e quale. */
    /* La scheda della colonna destra, chiesta «completa» il 04/11/2026:
       i tre blocchi di «E intanto si vende» e di OI-353919, con le
       etichette adattate al tema. Voci ricavate dal testo del pezzo, non
       aggiunte; scritte da Claude, da confermare dalla redazione. */
    frattura: {
      sinistra: {
        nome: "Il prodotto",
        voci: ["ricerca", "macchinari", "competenze", "qualità"],
      },
      destra: {
        nome: "La rendita",
        voci: ["prestigio già costruito", "prezzo alto", "materie prime ridotte", "interessi e commissioni"],
      },
    },
    catena: [
      "Adempimenti e controlli",
      "Meno investimenti nel prodotto",
      "Il fondo che compra il marchio",
      "Il prestigio senza la qualità",
      /* Anello aggiunto il 04/11/2026 su richiesta: dal primo capoverso,
         il cliente che riconosce il logo ma non la qualità. */
      "Il cliente che vede solo il logo",
      "Lo spazio per il falso",
    ],
    siCompra: [
      "origine riconoscibile",
      "autenticità verificabile",
      "provenienza dimostrabile",
      "norme applicate allo stesso modo",
    ],
    etichette: {
      catena: "La catena del falso",
      siCompra: "Che cosa serve",
    },
    controdomanda:
      "E se il falso prosperasse non perché i marchi hanno smesso di investire, ma perché chi compra ha smesso di pagare la differenza fra un prodotto e il suo segno?",
    corpo: [
      "La contraffazione diventa più difficile da riconoscere quando la copia riproduce con precisione i dettagli visibili dell’originale. In alcuni casi può persino apparire più curata. Questo mette in luce una vulnerabilità dei marchi. Se il cliente riconosce il logo ma fatica a riconoscere la qualità che giustifica il prezzo, il falsario può vendere una parte di ciò che il cliente cerca spendendo molto meno per produrla.",
      /* Due sottotitoli aggiunti il 04/11/2026 su richiesta («Nel testo»
         fino a cinque parti), questo e «Il costo del finanziamento» più
         sotto: scritti da Claude, da confermare. Il testo non cambia, il
         primo capoverso resta senza titolo come su OI-353919. */
      { tipo: "sezione", titolo: "Quando il marchio vale più del prodotto" },
      "Una parte di questa vulnerabilità nasce dentro le aziende. Molti marchi oggi conosciuti nel mondo devono il loro prestigio a imprenditori che per anni hanno investito in ricerca, macchinari e competenze. La loro capacità di anticipare il gusto si traduceva in prodotti riconoscibili per come erano fatti. Quel legame si indebolisce quando il prestigio accumulato negli anni permette di mantenere alto il prezzo anche dopo aver ridotto gli investimenti nella qualità.",

      { tipo: "sezione", titolo: "Dalla produzione alla rendita finanziaria" },
      "Il peso degli adempimenti amministrativi in Europa ha spinto molte imprese a rivedere processi e lavorazioni per restare sul mercato. Per una piccola azienda, il tempo e le risorse assorbiti da certificazioni e controlli incidono direttamente sulla capacità di investire nel prodotto. Il problema si aggrava quando uffici diversi applicano la stessa norma in modo diverso. L’impresa fatica a prevedere quali requisiti dovrà rispettare e quanto le costerà farlo.",
      "Su questa pressione si innesta la finanza speculativa. Quando un fondo acquisisce un marchio e punta a rilanciarlo riducendo il costo delle materie prime mentre mantiene alto il prezzo, il rendimento dipende sempre più dal prestigio già costruito. È una forma di cannibalismo economico.",
      /* La citazione, chiesta dal committente il 04/11/2026 («la frase tra
         virgolette» degli altri editoriali): come su OI-353919 è una frase
         del testo spostata fuori dal suo capoverso, al suo posto, non una
         frase aggiunta. Scelta da Claude, da confermare. */
      {
        tipo: "citazione",
        testo: "Si monetizza la reputazione del marchio riducendo proprio gli investimenti che l’hanno resa possibile.",
      },
      { tipo: "sezione", titolo: "Il costo del finanziamento" },
      "La pressione finanziaria si avverte anche nell’automobile, nell’edilizia e nel mercato dell’usato. Quando la vendita ruota attorno al finanziamento, interessi e commissioni possono far salire il costo finale per il compratore. In questi casi, per capire quanto costa davvero un bene, occorre guardare al totale pagato oltre che al prezzo esposto.",

      { tipo: "sezione", titolo: "Dove si apre lo spazio per il falso" },
      "Moda. La globalizzazione e l’esternalizzazione delle forniture su commessa hanno alimentato la falsificazione del design, da una parte, e dall’altra hanno danneggiato la produzione artigianale tramandata di generazione in generazione, privando l’industria italiana di una parte essenziale del suo budget produttivo.",
      "Automobile. Certificazioni, procedure rigide e sostituzioni di componenti ancora utilizzabili possono far lievitare il costo della manutenzione nelle reti ufficiali. Questo spinge una parte dei clienti verso il mercato indipendente, dove si trovano anche ricambi legittimi a prezzi inferiori. Il rischio di contraffazione emerge quando un pezzo viene presentato come originale senza esserlo. Prezzi alti e scarsa trasparenza sulla provenienza rendono più difficile per il cliente valutare l’offerta.",
      "Alimentare. Nomi italianizzati, confezioni e richiami geografici possono suggerire un’origine italiana che il prodotto non ha. È il fenomeno dell’Italian sounding. La contraffazione riguarda invece, per esempio, l’uso abusivo di un marchio o di una denominazione protetta. In entrambi i casi si sfrutta una reputazione costruita da altri. A subirne il peso sono anche le piccole e medie imprese che investono nella qualità, talvolta superiore a quella dei grandi marchi, ma hanno meno risorse per difendere il proprio nome e rendere riconoscibile l’origine dei prodotti.",

      /* «La controdomanda» (il campo qui sopra), prima dell'ultima parte
         come nel modello e in «E intanto si vende». */
      { tipo: "controdomanda" },

      { tipo: "sezione", titolo: "Il punto critico" },
      "Le piccole imprese subiscono una doppia pressione. Da una parte sostengono i costi degli adempimenti, dall’altra devono difendere il proprio lavoro da chi ne sfrutta il nome o ne imita il prodotto. Per chi compra, riconoscere l’origine e verificare l’autenticità resta spesso difficile. I singoli interventi di contrasto possono togliere dal mercato una partita di merce falsa. La vulnerabilità resta finché imitare un segno riconoscibile è più facile che dimostrare, al momento dell’acquisto, la qualità e la provenienza di ciò che si vende.",
    ],
  },
  {
    id: "frattura",
    titolo: "E intanto si vende",
    sommario:
      "Non è un fenomeno locale e non è una fase: tra chi produce ricchezza e chi stabilisce le condizioni in cui quella ricchezza viene prodotta si è aperta una distanza che nessuno sta più provando a ricomporre.",
    categoria: "Economia",
    paese: null,
    foto: null,
    /* L'illustrazione approvata il 02/10/2026
       (uploads/Consegna_Editoriale_articolo_OI.zip,
       Editoriale_cibernetica_senza_marchio_OI.png, copiata tale e quale).
       È un'immagine generata e astratta, non una fotografia del fatto:
       l'alt la descrive così. Nessun marchio né testo sopra. */
    copertina: {
      src: "/editoriale-cibernetica-dell-informazione.png",
      larghezza: 1983,
      altezza: 793,
      alt: "Illustrazione astratta della cibernetica dell’informazione: frammenti sparsi attraversano lastre di vetro e si ricompongono in una forma ordinata.",
    },
    pubblicato: "2026-09-20",
    settimaneFa: 0,
    provvisorio: false,
    /* La controdomanda del pezzo, scritta il 30/09/2026 per il campo
       Strapi `controdomanda` (156 caratteri, tetto 220). Rovescia le due
       colonne della tesi: la colpa sta nelle istituzioni, e la cessione
       arriva da fuori. Quando il pezzo passa su Strapi va copiata là
       tale e quale. */
    /* La scheda della colonna destra, scritta dalla redazione per questo
       pezzo (uploads/oi-istruzioni-editoriale-articolo 02.md, § 3.1). */
    frattura: {
      sinistra: {
        nome: "Imprese",
        voci: ["stabilità", "regole prevedibili", "rapidità decisionale", "accesso al credito"],
      },
      destra: {
        nome: "Istituzioni",
        voci: ["consenso", "regolamentazione", "sicurezza", "equilibrio sociale"],
      },
    },
    catena: [
      "Fondi cinesi e indiani",
      "Società dell'Est, veicoli intermedi",
      "Imprese medie europee",
      "La cessione",
    ],
    siCompra: ["competenza", "certificazioni", "brevetti", "clienti", "reputazione"],
    controdomanda:
      "E se il vuoto non fosse nelle istituzioni ma nelle imprese, che hanno rinviato successione e investimenti finché l'unica offerta rimasta è stata un assegno?",
    corpo: [
      "Tra imprese e istituzioni si è aperta una frattura. Non è un fenomeno locale e non è una fase: attraversa economie sviluppate ed emergenti e segna la fine di un rapporto che si dava per acquisito.",
      "Le imprese chiedono stabilità, regole prevedibili, rapidità decisionale, accesso al credito. Le istituzioni rispondono a tutt'altro: consenso, regolamentazione, sicurezza, equilibrio sociale.",
      "Il risultato è la distanza crescente tra chi produce ricchezza e chi stabilisce le condizioni in cui quella ricchezza viene prodotta.",

      { tipo: "sezione", titolo: "La perdita di fiducia" },
      "Il problema non è economico. È di fiducia.",
      "Quando le regole cambiano in corsa e le decisioni politiche ignorano la realtà produttiva, l'impresa riduce l'esposizione al rischio. Investimenti rinviati, assunzioni prudenti, liquidità ferma.",
      "Non è pessimismo. È l'unica risposta razionale a un interlocutore inaffidabile.",

      { tipo: "sezione", titolo: "Due linguaggi sempre più lontani" },
      "Il privato ragiona su costi, ricavi, competitività, rischio. Le istituzioni su consenso, redistribuzione, stabilità.",
      "I due linguaggi convivono solo se esiste un meccanismo di traduzione. Quel meccanismo non funziona più.",
      "Così l'impresa vede lo Stato come un ostacolo, e lo Stato vede l'impresa come un soggetto da regolamentare.",

      { tipo: "sezione", titolo: "Una concorrenza che non ha le stesse regole" },
      "Mentre la politica si misura sull'influenza e sul consenso, le imprese vedono il proprio mercato cannibalizzato da colossi che operano con regole diverse.",
      {
        tipo: "citazione",
        testo:
          "Fiscalità diversa, costo del lavoro diverso, vincoli diversi, scala diversa. Non è competizione: è asimmetria.",
      },
      "Chi rispetta le regole compete in svantaggio contro chi le aggira o se le fa scrivere addosso. E le istituzioni, che quelle regole dovrebbero riequilibrare, restano ferme.",

      {
        tipo: "riquadro",
        etichetta: "L'allarme",
        titolo: "L'allarme che nessuno registra",
        testo: [
          "C'è un passaggio ulteriore, ed è quello che dovrebbe preoccupare davvero.",
          "Fondi cinesi e indiani si muovono sulle imprese europee. Non direttamente: attraverso società dell'Est, veicoli intermedi, strutture che rendono difficile risalire al titolare effettivo.",
          "L'offerta è sempre la stessa: cifre importanti, subito, per consegnare l'azienda. Non un investimento, non una partnership. La cessione.",
          "I bersagli non sono casuali. Sono imprese con storia, filiera e know-how consolidato. Si compra esattamente quello che non si riesce a costruire: competenza, certificazioni, brevetti, clienti, reputazione.",
          "E l'offerta arriva nel momento in cui quelle imprese sono più fragili: margini compressi, credito difficile, successione incerta, nessuna prospettiva di sostegno pubblico. In quelle condizioni un assegno non è una tentazione. È l'unica via d'uscita visibile.",
          "Per le istituzioni, invece, è tutto sotto controllo. Le procedure ci sono, i poteri speciali esistono, i monitoraggi sono attivi.",
          "Sulla carta. Nei fatti si presidia l'acquisizione grande e visibile, mentre passa indisturbata quella diffusa, silenziosa, fatta di aziende medie che nessuno considera strategiche fino al giorno in cui non ci sono più.",
          "Non è una perdita di quote di mercato. È un trasferimento di patrimonio industriale che non si recupera.",
        ],
      },

      { tipo: "sezione", titolo: "Un sistema diventato autoreferenziale" },
      "Le politiche economiche vengono elaborate senza confronto reale con chi opera sul mercato. Le decisioni si giustificano tra loro, non con i risultati.",
      "Nel frattempo le imprese si adattano da sole.",
      "Esistono ormai due economie parallele: quella descritta dalle istituzioni e quella vissuta ogni giorno da chi produce. Non comunicano. E la seconda non aspetta più la prima.",

      { tipo: "sezione", titolo: "Il vuoto lasciato dai corpi intermedi" },
      "Questa frattura ha una causa precisa: il collasso dei corpi intermedi.",
      "Partiti, sindacati, associazioni di categoria, ordini professionali avevano una funzione sola, tenere collegati i due mondi. Hanno perso credibilità e hanno smesso di svolgerla.",
      "I partiti hanno perso radicamento produttivo. I sindacati difendono un perimetro sempre più stretto. Le associazioni di categoria sono diventate fornitori di servizi ai propri iscritti.",
      "Il risultato è la disintermediazione: tratta direttamente chi ha dimensione e accesso. Tutti gli altri non hanno più rappresentanza. E nessuno, oggi, avverte l'imprenditore che quell'offerta non è un'opportunità.",

      /* «La controdomanda» (il campo `controdomanda` qui sopra) sta qui,
         fra il vuoto dei corpi intermedi e la questione finale. */
      { tipo: "controdomanda" },

      { tipo: "sezione", titolo: "La vera questione" },
      "Il punto non è se lo Stato debba intervenire di più o di meno. È se imprese e istituzioni si riconoscano ancora come parti dello stesso sistema.",
      "Quando il sistema produttivo perde fiducia nelle istituzioni e le istituzioni perdono di vista il sistema produttivo, il problema smette di essere economico. Diventa una frattura strutturale nel modello di sviluppo.",
      "Il danno è ormai sistemico. Per ricomporlo serviranno anni di regole stabili e di rappresentanze ricostruite.",

      {
        tipo: "chiusa",
        testo: "Nessuno, oggi, ha intenzione di cominciare. E intanto si vende.",
      },
    ],
  },
  {
    id: "demo-01",
    titolo: "Le rotte non si spostano per decreto",
    sommario:
      "Un corridoio commerciale cambia quando cambiano i costi, non quando viene annunciato. Cosa guardare per capire se uno spostamento è reale.",
    categoria: "Economia",
    paese: "Egitto",
    /* Una delle foto dell'archivio dei dispacci, presa come le altre di
       questo file: non ha niente a che vedere con il testo e serve solo
       a vedere in pagina il caso "editoriale con foto". */
    foto: "https://static-files.osservatorioitaliano.org/20260616/6w5gV3V7Cc",
    settimaneFa: 1,
    corpo: [
      "Ogni volta che viene annunciato un nuovo corridoio commerciale, la domanda giusta non è se l'annuncio sia autorevole, ma se qualcuno abbia già cominciato a pagare per usarlo. Le rotte sono scelte economiche prima che scelte politiche: si spostano quando spostarsi costa meno che restare, e non un giorno prima.",
      "Per capire se uno spostamento è reale conviene guardare tre cose. Chi assicura il transito e a che prezzo, perché il premio è la misura più onesta del rischio percepito. Quanto tempo impiega davvero una spedizione, non quanto ne impiegherebbe sulla carta. E se accanto al tracciato stiano nascendo magazzini, dogane e officine, che nessuno costruisce per una rotta che non crede duratura.",
      "Quando queste tre cose si muovono insieme, il corridoio esiste anche se non lo ha inaugurato nessuno. Quando si muove solo il comunicato, di solito quello che si è spostato è l'attenzione, non le merci.",
    ],
    /* Due foto, ancorate a paragrafi lontani: due ancoraggi vicini si
       sovrappongono, perché nel margine le foto non allungano il testo.
       Le didascalie sono di lunghezza diversa apposta, corta e lunga,
       come per i titoli: una didascalia di due righe e una di quattro
       occupano il margine in modo molto diverso e vanno viste tutte e
       due. Le foto sono prese dall'archivio dei dispacci e non hanno
       niente a che vedere con questo testo: per questo il credito dice
       che sono provvisorie. */
    immagini: [
      {
        src: "https://static-files.osservatorioitaliano.org/20260616/6w5gV3V7Cc",
        didascalia:
          "Il premio di un'assicurazione è la misura più onesta del rischio percepito.",
        credito: "Immagine provvisoria",
        dopoParagrafo: 1,
      },
      {
        src: "https://static-files.osservatorioitaliano.org/20260729/2yL6TAxZ5i",
        didascalia:
          "Magazzini, dogane e officine lungo un tracciato: nessuno li costruisce per una rotta che non crede duratura, ed è per questo che si guardano prima degli annunci.",
        credito: "Immagine provvisoria",
        dopoParagrafo: 3,
      },
    ],
  },
  {
    id: "demo-02",
    titolo: "Chi compra il rischio",
    sommario:
      "Assicurazioni, garanzie pubbliche, coperture private: dietro ogni operazione estera c'è qualcuno che si prende il rischio, e sapere chi cambia la lettura.",
    categoria: "Finanza",
    paese: "Germania",
    foto: null,
    settimaneFa: 2,
    corpo: [
      "Dietro ogni operazione all'estero c'è sempre qualcuno che si assume il rischio al posto di qualcun altro. Può essere un assicuratore privato, una garanzia pubblica all'esportazione, una banca che sconta una fattura o, più spesso di quanto si pensi, il fornitore stesso, che accetta di essere pagato tardi. Sapere chi è cambia completamente la lettura dell'operazione.",
      "Il motivo è semplice: chi porta il rischio detta le condizioni. Se la copertura viene da un ente pubblico, la scelta del paese risponde anche a una priorità di politica industriale. Se viene dal mercato, il prezzo della copertura racconta quanto quel mercato consideri fragile la controparte, e lo racconta meglio di qualunque analisi qualitativa.",
      "Per questo, leggendo un'operazione, conviene partire dalla fine: non da chi firma il contratto, ma da chi paga se il contratto salta.",
    ],
  },
  {
    id: "demo-03",
    titolo:
      "L'ordine dei dati viene prima dell'automazione, e quasi nessuno lo verifica",
    sommario:
      "Prima di chiedersi quale strumento adottare conviene chiedersi come si muove l'informazione dentro l'impresa. L'automazione amplifica l'ordine che trova.",
    categoria: "Industria",
    paese: null,
    foto: null,
    settimaneFa: 3,
    corpo: [
      "La domanda che arriva quasi sempre per prima è quale strumento adottare. È la domanda sbagliata, o almeno è la seconda. La prima è come si muove oggi l'informazione dentro l'impresa: chi la produce, dove si ferma, quante volte viene ricopiata a mano da un sistema all'altro.",
      "L'automazione non mette ordine, amplifica l'ordine che trova. Su un processo pulito fa risparmiare tempo in modo evidente. Su un processo confuso rende la confusione più veloce e più difficile da vedere, perché la nasconde dentro un flusso che nessuno guarda più.",
      "Prima di scegliere un fornitore vale la pena scrivere su un foglio il percorso di un singolo dato, da dove nasce a dove viene usato per decidere qualcosa. Se il percorso non sta su un foglio, il problema non è lo strumento.",
    ],
  },
  {
    id: "demo-04",
    titolo:
      "Le materie prime non sono una lista di minerali ma una catena di contratti, raffinerie e tempi",
    sommario:
      "Contano i contratti, le raffinerie e i tempi di consegna, non l'elenco dei minerali. Dove si forma davvero la dipendenza di una filiera.",
    categoria: "Materie prime",
    paese: "Cile",
    foto: null,
    settimaneFa: 4,
    corpo: [
      "L'elenco dei minerali critici è diventato un genere letterario. È anche il modo meno utile di guardare la questione, perché un elenco non dice niente su dove si formi davvero la dipendenza di una filiera.",
      "La dipendenza non sta nella miniera, che è quasi sempre più diffusa di quanto si creda. Sta nella raffinazione, che è concentrata in pochi impianti, nei contratti pluriennali che ne impegnano la capacità in anticipo, e nei tempi di consegna, che decidono quanto margine di manovra resti quando qualcosa si inceppa.",
      "Una filiera si valuta chiedendosi quanto tempo servirebbe per sostituire ciascun passaggio, non quanto è raro il materiale di partenza. Quasi sempre il collo di bottiglia è un passaggio intermedio che nessuno aveva messo in elenco.",
    ],
  },
  {
    id: "demo-05",
    titolo: "Due diligence non è un documento",
    sommario:
      "Verificare una controparte vuol dire ricostruire chi decide, non archiviare un fascicolo. Le domande che restano aperte quando il fascicolo è chiuso.",
    categoria: "Giuridico",
    paese: "Emirati Arabi Uniti",
    foto: null,
    settimaneFa: 5,
    corpo: [
      "La due diligence viene spesso archiviata come un adempimento: si raccoglie la documentazione, si compila un fascicolo, si chiude. Verificare una controparte è un'altra cosa, ed è ricostruire chi decide davvero.",
      "Le domande che contano sono poche e restano aperte anche a fascicolo chiuso. Chi ha il potere di firmare e chi quello di far cambiare idea a chi firma. Da dove arrivano i soldi che finanziano l'operazione e a chi tornano. Cosa succede al rapporto se cambia l'assetto societario o se cambia il quadro normativo del paese.",
      "Un fascicolo completo che non risponde a queste domande protegge dal punto di vista formale e non protegge dal punto di vista sostanziale. È utile saperlo prima, non dopo.",
    ],
  },
  {
    id: "demo-06",
    titolo:
      "Il porto è un pezzo di politica industriale, e le concessioni lo dicono prima dei comunicati",
    sommario:
      "Concessioni, dragaggi, retroporti: le decisioni portuali si leggono come scelte industriali e diplomatiche insieme.",
    categoria: "Logistica",
    paese: "Grecia",
    /* Il secondo pezzo con foto: due su dieci sono già abbastanza per
       vedere in elenco come stanno insieme le schede illustrate e
       quelle di solo testo. */
    foto: "https://static-files.osservatorioitaliano.org/20260623/VtQJbDHqGH",
    settimaneFa: 6,
    corpo: [
      "Le decisioni portuali vengono raccontate come questioni tecniche: profondità dei fondali, lunghezza delle banchine, capacità dei piazzali. Sono anche, e spesso soprattutto, scelte industriali e diplomatiche.",
      "Una concessione decide chi opera un terminale per i prossimi decenni, e quindi quali rotte troveranno servizio conveniente e quali no. Un dragaggio decide quale classe di navi può entrare, e quindi a quale rete globale il porto appartiene. Un retroporto decide se la merce si ferma a creare lavoro o se passa e basta.",
      "Per questo conviene leggere le decisioni portuali con lo stesso metro con cui si leggono gli accordi commerciali, guardando chi ottiene cosa e per quanto tempo, invece che il dettaglio ingegneristico.",
    ],
    /* Una foto sola e senza credito: serve a vedere il caso in cui
       sotto la didascalia non c'è nient'altro, che è il più frequente
       quando l'immagine è nostra. */
    immagini: [
      {
        src: "https://static-files.osservatorioitaliano.org/20260623/VtQJbDHqGH",
        didascalia:
          "Un retroporto decide se la merce si ferma a creare lavoro o se passa e basta.",
        credito: null,
        dopoParagrafo: 1,
      },
    ],
  },
  {
    id: "demo-07",
    titolo: "Segnali deboli e falsi allarmi",
    sommario:
      "Come si distingue un'avvisaglia da un rumore di fondo. Tre criteri che usiamo prima di dare peso a una notizia isolata.",
    categoria: "Metodo",
    paese: null,
    foto: null,
    settimaneFa: 7,
    corpo: [
      "Il problema dei segnali deboli non è trovarli, è distinguerli dal rumore. Una notizia isolata è quasi sempre solo una notizia isolata, e trattarla come un'avvisaglia produce allarmi che poi nessuno ricorda di aver sbagliato.",
      "Prima di dare peso a un segnale usiamo tre criteri. La ripetizione: lo stesso fenomeno compare in luoghi o settori che non comunicano fra loro. La coerenza: quello che viene detto coincide con quello che viene fatto, per esempio con una spesa o con un'assunzione. E la scomodità: il segnale arriva da qualcuno che non ha interesse a diffonderlo.",
      "Quando i tre criteri reggono insieme vale la pena guardare meglio. Quando ne regge uno solo, di solito quello che abbiamo trovato è un'opinione ben scritta.",
    ],
  },
  {
    id: "demo-08",
    titolo: "Le sanzioni si leggono al contrario",
    sommario:
      "Non conta solo cosa vietano, ma cosa lasciano aperto e chi ne approfitta. La geografia delle esenzioni racconta più dell'elenco dei divieti.",
    categoria: "Sanzioni",
    paese: "Turchia",
    foto: null,
    settimaneFa: 8,
    corpo: [
      "Un pacchetto di sanzioni si legge male partendo dall'elenco dei divieti. Si legge molto meglio partendo da quello che lascia aperto, perché è lì che si sposta il traffico.",
      "Le esenzioni non sono dettagli tecnici, sono la mappa delle dipendenze che chi decide non se la sente di interrompere. Le soglie sotto le quali un'operazione resta lecita disegnano la dimensione media degli operatori che continueranno a lavorare. I periodi di transizione dicono quanto tempo si è concesso al proprio sistema produttivo per riorganizzarsi.",
      "Messe insieme, queste tre cose raccontano gli obiettivi reali di una misura meglio del comunicato che la accompagna, e permettono di prevedere dove nasceranno le rotte alternative.",
    ],
  },
  {
    id: "demo-09",
    titolo: "Presenza non è esportazione",
    sommario:
      "Aprire un mercato e tenerlo sono due mestieri diversi. Cosa serve al secondo, e perché si sottovaluta.",
    categoria: "Presenza internazionale",
    paese: "Vietnam",
    foto: null,
    settimaneFa: 9,
    corpo: [
      "Aprire un mercato e tenerlo sono due mestieri diversi, e il secondo viene sistematicamente sottovalutato. L'apertura è un progetto con un inizio e una fine: si trova il compratore, si firma, si consegna. La presenza è una condizione permanente e ha costi ricorrenti.",
      "Tenere un mercato vuol dire avere qualcuno che risponde nel fuso orario giusto, ricambi disponibili senza attendere una spedizione, assistenza che parli la lingua del cliente e un rapporto con chi regola il settore. Sono voci di spesa che non producono un ordine immediato e che per questo vengono tagliate per prime.",
      "Il risultato tipico è un'azienda che vanta esportazioni verso molti paesi ed è realmente presente in pochissimi. La differenza si vede solo quando arriva un concorrente che sul posto c'è.",
    ],
  },
  {
    id: "demo-10",
    titolo: "Una filiera si misura dai fornitori piccoli",
    sommario:
      "La resistenza di una catena di fornitura non sta nei grandi impianti ma nella rete di aziende che li tengono in piedi.",
    categoria: "Industria",
    paese: "Polonia",
    foto: null,
    settimaneFa: 10,
    corpo: [
      "Quando si valuta la solidità di una catena di fornitura si guardano i grandi impianti, perché sono visibili e misurabili. La resistenza però quasi mai sta lì.",
      "Sta nella rete di aziende piccole che fanno una lavorazione sola, spesso insostituibile nel breve periodo, e che nessuno ha in elenco perché fatturano poco. Sono anche quelle con meno margine per assorbire uno shock: un'interruzione degli ordini, un aumento del costo dell'energia o un passaggio generazionale mal preparato bastano a farle chiudere.",
      "Mappare i fornitori piccoli è noioso e costa tempo. È anche l'unico modo per sapere in anticipo dove una filiera si spezzerà, invece di scoprirlo quando si è già spezzata.",
    ],
  },
];

/** Una settimana in millisecondi: il passo con cui i provvisori si
 *  distanziano fra loro. */
const SETTIMANA = 7 * 24 * 60 * 60 * 1000;

/** L'istante di una data scritta `AAAA-MM-GG`, o `null` se non è una
 *  data.
 *
 *  A mezzogiorno e non a mezzanotte, e non è un vezzo: chi stampa la
 *  data (`fmtDate`, `dataEstesa`) legge `getDate()`, cioè il giorno
 *  nel fuso dell'orologio di sistema. Con la mezzanotte UTC basta un
 *  fuso a occidente perché il 20 settembre esca come il 19. */
function istanteDi(iso: string | undefined): number | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const t = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12).getTime();
  return Number.isNaN(t) ? null : t;
}

/** La data di pubblicazione dichiarata dal pezzo, quando c'è.
 *
 *  Separata da `tempoEditoriale` perché serve anche a chi non deve
 *  sapere che ora è: i metadata dichiarano `publishedTime` soltanto
 *  sui pezzi che una data ce l'hanno, mentre la data calcolata di un
 *  provvisorio, cambiando ogni giorno, in un metadato sarebbe una
 *  data falsa. */
export function dataPubblicazione(e: Pick<EditorialeDemo, "pubblicato">): number | null {
  return istanteDi(e.pubblicato);
}

/** L'ultima uscita vera, cioè la più recente delle date dichiarate. */
const ULTIMA_USCITA: number | null = EDITORIALI_DEMO.reduce<number | null>((max, e) => {
  const t = istanteDi(e.pubblicato);
  return t === null ? max : max === null || t > max ? t : max;
}, null);

/** L'istante di pubblicazione di un pezzo, comunque lo si ricavi.
 *
 *  Chi una data ce l'ha scritta tiene quella. Gli altri sono
 *  provvisori e la loro data si conta a ritroso da **l'ultima uscita
 *  vera**, non da adesso: così l'elenco resta in ordine anche fra un
 *  mese, quando un provvisorio contato da oggi risulterebbe più
 *  recente del pezzo pubblicato che gli sta sopra. Senza nessuna
 *  uscita vera (è il caso di un repo di soli provvisori) si riparte da
 *  `adesso`, che è il comportamento di prima. */
export function tempoEditoriale(e: EditorialeDemo, adesso: number): number {
  const fissa = istanteDi(e.pubblicato);
  if (fissa !== null) return fissa;
  return (ULTIMA_USCITA ?? adesso) - e.settimaneFa * SETTIMANA;
}
